import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { CheckCircle, AlertCircle, Info, Accessibility, Shield, Star, Bot, ShieldCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { MoreThanHpiModal } from "./MoreThanHpiModal";
import { CameraScanButton } from "./CameraScanButton";
import { isServiceModeCode, verifyServiceCode, getServiceModeSession, clearServiceMode } from "../utils/serviceMode";
import { UK_PLATE_RE } from "../utils/ukPlate";

/**
 * Landing form (the only thing visible before any analysis is started).
 *
 * Pure presentation: all state lives in <Home/>. Callbacks update state there.
 * The component is responsible for VIN preview fetching since it's purely
 * a side effect of typing in the VIN field.
 */
export const LandingForm = ({
  t,
  API,
  // form state
  registration,
  setRegistration,
  targetPrice,
  setTargetPrice,
  vinNumber,
  setVinNumber,
  vinError,
  setVinError,
  vinPreview,
  setVinPreview,
  vinPreviewLoading,
  setVinPreviewLoading,
  // status
  loading,
  error,
  // actions
  onSubmit,
  onKeyPress,
  setError,
}) => {
  const [hpiInfoOpen, setHpiInfoOpen] = useState(false);
  const [regInputError, setRegInputError] = useState(false);

  // ─── Service Mode (staff / QA paywall bypass) ─────────────────
  const [serviceActive, setServiceActive] = useState(() => !!getServiceModeSession());
  const [serviceVerifying, setServiceVerifying] = useState(false);
  const [serviceError, setServiceError] = useState("");
  const [serviceJustActivated, setServiceJustActivated] = useState(false);

  useEffect(() => {
    // Re-check on mount in case another tab activated / expired it.
    setServiceActive(!!getServiceModeSession());
  }, []);

  // When the raw input looks like a service-mode code we PRESERVE the
  // hyphens so the user can paste `WSVC-XXXX-XXXX-XXXX-XXXX` cleanly.
  const sanitizeReg = (raw) => {
    const upper = (raw || "").toUpperCase();
    if (isServiceModeCode(upper)) {
      // Keep hyphens + alphanumerics only, cap at 24 chars.
      return upper.replace(/[^A-Z0-9-]/g, '').slice(0, 24);
    }
    return upper.replace(/[^A-Z0-9]/g, '').slice(0, 7);
  };

  const handleRegistrationChange = (e) => {
    const raw = e.target.value;
    const clean = sanitizeReg(raw);
    setRegistration(clean);
    // Clear sticky server-side error as soon as user starts editing again.
    if (setError) setError("");
    if (serviceError) setServiceError("");
    // Flag invalid characters only if user typed them. Allow hyphens for
    // service-code entry.
    const allowHyphen = isServiceModeCode(raw);
    const bad = allowHyphen ? /[^A-Z0-9\s-]/i.test(raw) : /[^A-Z0-9\s]/i.test(raw);
    setRegInputError(bad);
  };

  const regHasContent = registration.length > 0;
  const regLooksLikeServiceCode = isServiceModeCode(registration);
  const regIsValid = regHasContent && (
    regLooksLikeServiceCode || UK_PLATE_RE.test(registration)
  );
  // Server/client rejected the plate format — highlight the input in red.
  const regRejected = error === 'errorInvalidRegistration';

  /**
   * Submit interceptor. If the input is a service-mode code we call the
   * backend to verify it — on success we flip into "service mode" and
   * clear the input for a real plate. Otherwise we fall through to the
   * regular analyse flow.
   */
  const handleCheckClick = async () => {
    if (regLooksLikeServiceCode) {
      setServiceVerifying(true);
      setServiceError("");
      try {
        const result = await verifyServiceCode(registration, API);
        if (result.ok) {
          setServiceActive(true);
          setServiceJustActivated(true);
          setRegistration("");
          setTimeout(() => setServiceJustActivated(false), 3500);
        } else {
          setServiceError(result.message || "Invalid code");
        }
      } finally {
        setServiceVerifying(false);
      }
      return;
    }
    onSubmit();
  };

  const handleExitServiceMode = () => {
    clearServiceMode();
    setServiceActive(false);
    setServiceJustActivated(false);
  };

  const handleVinChange = (e) => {
    const raw = e.target.value
      .replace(/\s/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 17);
    setVinNumber(raw);
    if (raw.length > 0 && raw.length !== 17) {
      setVinError(t('vinInvalid'));
      setVinPreview(null);
    } else {
      setVinError("");
      if (raw.length !== 17) setVinPreview(null);
    }
    if (raw.length === 17) {
      setVinPreviewLoading(true);
      setVinPreview(null);
      axios.get(`${API}/vin/decode/${raw}`)
        .then((res) => {
          if (res.data && res.data.ok) setVinPreview(res.data);
          else setVinPreview({ ok: false });
        })
        .catch(() => setVinPreview({ ok: false }))
        .finally(() => setVinPreviewLoading(false));
    }
  };

  return (
    <motion.div
      key="search-form"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-xl lg:max-w-5xl xl:max-w-6xl mx-auto text-center"
      style={{ marginTop: 'clamp(80px, 12vh, 140px)' }}
    >
      <motion.div
        className="rounded-[32px] pt-10 sm:pt-12 px-6 sm:px-8 pb-8 sm:pb-10 panel-premium landing-glass-panel lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center lg:px-12 lg:py-14"
        style={{
          background: 'var(--wf-panel-bg)',
          border: '1px solid var(--wf-panel-border)',
          boxShadow: 'var(--wf-panel-shadow)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          marginLeft: '-24px',
          marginRight: '-24px',
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="px-2 sm:px-4 pb-8 lg:pb-0 lg:px-2 lg:text-left">
          <motion.h2
            className="text-[38px] sm:text-[46px] lg:text-[56px] font-bold mb-4 leading-tight text-gradient-premium"
            style={{ letterSpacing: '-0.03em', paddingBottom: '0.1em' }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span>{t('mainHeading')}</span>
          </motion.h2>
          <motion.div
            className="space-y-1.5 mt-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {[t('heroLine1'), t('heroLine2')].map((line, index, arr) => (
              <p
                key={index}
                className={index === arr.length - 1 ? "text-base sm:text-lg font-semibold" : "text-base sm:text-lg"}
                style={{ color: index === arr.length - 1 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.85)', lineHeight: '1.5' }}
              >
                {line}
              </p>
            ))}
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            className="rounded-[24px] p-6 sm:p-10 glass-surface-dark relative z-10 transition-colors duration-700"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.70) 0%, rgba(15, 23, 42, 0.40) 55%, rgba(15, 23, 42, 0.08) 100%)',
              border: regIsValid ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: regIsValid 
                ? '0 20px 50px -15px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(52, 211, 153, 0.15), 0 0 40px -10px rgba(52, 211, 153, 0.15)' 
                : '0 20px 50px -15px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="space-y-5 relative z-10">
            {/* Service Mode banner — visible when active */}
            {serviceActive && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl"
                style={{
                  background: 'linear-gradient(90deg, rgba(16,185,129,0.18), rgba(20,184,166,0.14))',
                  border: '1px solid rgba(52,211,153,0.4)',
                  boxShadow: '0 0 30px -5px rgba(16,185,129,0.35)',
                }}
                role="status"
                aria-live="polite"
                data-testid="service-mode-banner"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{ color: '#34d399' }} />
                  <div className="min-w-0 text-left">
                    <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: '#a7f3d0', letterSpacing: '0.08em' }}>
                      {serviceJustActivated ? 'Service Mode Activated' : 'Service Mode Active'}
                    </div>
                    <div className="text-[11px] leading-tight" style={{ color: 'rgba(226,232,240,0.85)' }}>
                      All paywalls bypassed · type any UK registration to continue
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleExitServiceMode}
                  className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-md flex-shrink-0 hover:bg-white/10 transition-colors"
                  style={{ color: 'rgba(226,232,240,0.7)', letterSpacing: '0.06em' }}
                  data-testid="service-mode-exit-btn"
                >
                  Exit
                </button>
              </motion.div>
            )}
            {serviceError && !serviceActive && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-4 py-2 rounded-xl text-center"
                style={{
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.35)',
                  color: '#FCA5A5',
                  fontSize: 12,
                  fontWeight: 600,
                }}
                data-testid="service-mode-error"
                role="alert"
              >
                {serviceError}
              </motion.div>
            )}
            {/* AI Floating Badge */}
            <motion.div
              animate={{ y: [-4, 4, -4] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute -top-12 -right-4 lg:-right-8 bg-emerald-500/10 border border-emerald-400/30 backdrop-blur-md rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-[0_0_20px_rgba(16,185,129,0.2)] z-20 pointer-events-none hidden sm:flex"
            >
              <Bot className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">AI Powered</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t('regHelperText')}
                  value={registration}
                  onChange={handleRegistrationChange}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCheckClick();
                    } else if (onKeyPress) {
                      onKeyPress(e);
                    }
                  }}
                  className="text-[17px] py-6 px-5 text-center uppercase font-bold tracking-widest focus:outline-none transition-all duration-300 placeholder:font-normal placeholder:tracking-normal placeholder:normal-case placeholder:text-[15px] placeholder:text-slate-400 glass-surface-dark focus-visible:ring-1 focus-visible:ring-emerald-500/50"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: (regInputError || regRejected) ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.12)',
                    color: (regInputError || regRejected) ? '#FCA5A5' : '#FFFFFF',
                    paddingRight: '52px',
                  }}
                  maxLength={32}
                  aria-invalid={regInputError || regRejected || (regHasContent && !regIsValid)}
                  data-testid="registration-input"
                />
                {/* Camera scan button — opens phone camera on mobile, OCRs the plate */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2" style={{ zIndex: 3 }}>
                  <CameraScanButton
                    mode="plate"
                    compact
                    t={t}
                    onScanned={(plate) => {
                      const clean = sanitizeReg(plate);
                      setRegistration(clean);
                      if (setError) setError("");
                      setRegInputError(false);
                    }}
                    data-testid="reg-camera-scan"
                  />
                </div>
              </div>
              {regInputError && (
                <p className="text-xs mt-1.5 text-center text-rose-200" data-testid="reg-input-error">
                  {t('errorInvalidPlateChars') || 'Only letters and numbers allowed in UK plates.'}
                </p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.60, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative">
                <span className="absolute left-5 top-1/2 transform -translate-y-1/2 text-lg font-bold" style={{ color: '#34D399', zIndex: 2 }}>£</span>
                <Input
                  type="number"
                  placeholder={t('priceHelperText')}
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(e.target.value)}
                  onKeyPress={onKeyPress}
                  className="text-[17px] py-6 pl-11 pr-5 text-center font-bold tracking-wide focus:outline-none transition-all duration-300 placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400 placeholder:text-[15px] glass-surface-dark focus-visible:ring-1 focus-visible:ring-emerald-500/50"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.12)',
                    color: "var(--wf-modal-text)",
                  }}
                  min="0"
                  step="100"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t('vinPlaceholder')}
                  value={vinNumber}
                  onChange={handleVinChange}
                  className="text-[14px] py-6 px-5 text-center uppercase tracking-wider font-mono font-semibold focus:outline-none transition-all duration-300 placeholder:font-normal placeholder:tracking-normal placeholder:normal-case placeholder:text-[14px] placeholder:text-slate-400 glass-surface-dark focus-visible:ring-1 focus-visible:ring-emerald-500/50"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: vinError ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.12)',
                    color: vinError ? '#FCA5A5' : '#FFFFFF',
                    paddingRight: '52px',
                  }}
                  maxLength={17}
                  data-testid="vin-input"
                />
                {vinNumber.length === 17 && (
                  <CheckCircle className="absolute right-12 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                )}
                {/* Camera scan button — opens phone camera, OCRs the VIN plate */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2" style={{ zIndex: 3 }}>
                  <CameraScanButton
                    mode="vin"
                    compact
                    t={t}
                    onScanned={(vin) => {
                      const clean = (vin || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17);
                      setVinNumber(clean);
                      if (clean.length > 0 && clean.length !== 17) {
                        setVinError(t('vinInvalid'));
                      } else {
                        setVinError("");
                      }
                    }}
                    data-testid="vin-camera-scan"
                  />
                </div>
              </div>
              {vinError && (
                <p className="text-xs text-amber-300 mt-1.5 text-center">{vinError}</p>
              )}
              {!vinError && vinNumber.length === 0 && t('vinHelperText') && (
                <p
                  className="text-xs mt-1.5 text-center font-mono tracking-wide"
                  style={{ color: "var(--wf-modal-text-muted)" }}
                  data-testid="vin-helper-text"
                >
                  {t('vinHelperText')}
                </p>
              )}
              {!vinError && vinNumber.length === 17 && (vinPreviewLoading || vinPreview) && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="mt-2 flex justify-center"
                  data-testid="vin-preview-wrapper"
                >
                  {vinPreviewLoading ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-slate-800/60 text-slate-300 border border-slate-700/50" data-testid="vin-preview-loading">
                      <span className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></span>
                      {t('vinPreviewLoading')}
                    </span>
                  ) : vinPreview && vinPreview.ok && (vinPreview.make || vinPreview.model) ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" data-testid="vin-preview-detected">
                      <CheckCircle className="w-3 h-3" />
                      <span>
                        {t('vinPreviewDetected')}:{' '}
                        <span className="font-bold tracking-wide">
                          {[vinPreview.make, vinPreview.model, vinPreview.year ? `(${vinPreview.year})` : null].filter(Boolean).join(' ')}
                        </span>
                      </span>
                    </span>
                  ) : vinPreview && !vinPreview.ok ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-800/40 text-slate-400 border border-slate-700/40" data-testid="vin-preview-unknown">
                      {t('vinPreviewUnknown')}
                    </span>
                  ) : null}
                </motion.div>
              )}
            </motion.div>

            {error && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">{t(error) || error}</AlertDescription>
                </Alert>
              </motion.div>
            )}

            {/* ─── Hero trust badge (Feb 2026 positioning) ──────────────────
                Communicates Worthify > a plain HPI check. Sits BETWEEN the
                form and the CTA so it's the last thing users read before
                tapping "Check the car". Glassmorphism pill with subtle pulse
                on first paint + info icon that opens the positioning modal. */}
            <motion.button
              type="button"
              onClick={() => setHpiInfoOpen(true)}
              data-testid="hero-hpi-badge"
              aria-label={t("heroHpiBadgeAria") || "Learn more about More Than HPI Check"}
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.35, type: "spring", damping: 22, stiffness: 280 }}
              whileHover={{ scale: 1.025, y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="hero-hpi-badge badge-breathe mx-auto flex items-center gap-2"
              style={{
                padding: "8px 14px 8px 12px",
                borderRadius: 9999,
                background: "var(--wf-modal-card-bg)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid rgba(148, 163, 184, 0.22)",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.18), inset 0 1px 0 rgba(255,255,255,0.06)",
                color: "var(--wf-modal-text)",
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: "-0.1px",
                cursor: "pointer",
              }}
            >
              <Shield
                aria-hidden
                className="w-3.5 h-3.5 flex-shrink-0"
                strokeWidth={2.6}
                style={{ color: "#34d399" }}
              />
              <span>{t("heroHpiBadgeText") || "More Than HPI Check"}</span>
              <Info
                className="w-3.5 h-3.5"
                strokeWidth={2.4}
                style={{ color: "rgba(241, 245, 249, 0.72)" }}
                aria-hidden
              />
            </motion.button>

            {/* H40l: "Already analysed N cars" counter removed per user request. */}

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="text-center"
              style={{ fontSize: 11.5, color: "var(--wf-modal-text-muted)", letterSpacing: "0.02em", fontWeight: 600 }}
              data-testid="hero-vehicle-types"
            >
              {t("heroVehicleTypes") || "Cars · Motorcycles · Vans · Trucks — any UK registration"}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.70, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="pt-2"
            >
              <Button
                onClick={handleCheckClick}
                disabled={loading || serviceVerifying}
                className="w-full text-slate-900 dark:text-white py-6 text-[17px] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed btn-depth btn-pulse-glow"
                style={{
                  minHeight: '60px',
                  letterSpacing: '0.01em',
                  borderRadius: '16px',
                }}
                data-testid="check-vehicle-btn"
              >
                {(loading || serviceVerifying) ? (
                  <span className="flex items-center justify-center gap-3">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                    <span>{serviceVerifying ? 'Verifying…' : t('checkingVehicle')}</span>
                  </span>
                ) : regLooksLikeServiceCode ? (
                  'Activate Service Mode'
                ) : (
                  t('analyzeButton')
                )}
              </Button>
            </motion.div>

            <motion.div
              className="flex items-center justify-center gap-2 mt-4"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.5 }}
            >
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wide uppercase badge-breathe text-center leading-tight"
                style={{
                  background: 'linear-gradient(90deg, rgba(52,211,153,0.18), rgba(16,185,129,0.24))',
                  color: '#a7f3d0',
                  border: '1px solid rgba(110,231,183,0.35)',
                  boxShadow: '0 0 18px rgba(16,185,129,0.22)',
                  letterSpacing: '0.05em',
                  maxWidth: '92%',
                  textWrap: 'balance',
                }}
              >
                <CheckCircle className="w-3 h-3 flex-shrink-0" style={{ color: '#34d399' }} />
                {t('ctaHelperText')}
              </span>
            </motion.div>
            {/* Disability-friendly hero badge */}
            <motion.div
              className="flex items-center justify-center mt-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.5 }}
            >
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wide uppercase text-center leading-tight"
                style={{
                  background: 'linear-gradient(90deg, rgba(45,212,191,0.16), rgba(14,165,233,0.18))',
                  color: '#99f6e4',
                  border: '1px solid rgba(45,212,191,0.35)',
                  boxShadow: '0 0 18px rgba(45,212,191,0.18)',
                  letterSpacing: '0.05em',
                  maxWidth: '92%',
                  textWrap: 'balance',
                }}
                data-testid="hero-disability-badge"
              >
                <Accessibility className="w-3 h-3 flex-shrink-0" style={{ color: '#2dd4bf' }} />
                {t('heroDisabilityBadge') || 'Designed for everyone'}
              </span>
            </motion.div>
            <p
              className="text-center font-medium mt-3"
              style={{ color: "var(--wf-modal-text-muted)", fontSize: '12px', letterSpacing: '0.03em' }}
            >
              {t('trustText')}
            </p>
          </div>
        </motion.div>
        </div>
      </motion.div>

      {/* ─── Trusted UK Data Infrastructure (Jun 2026) ──────────
          Title + subtitle sit ABOVE the full-bleed glass bar; the bar holds
          only the logos. MIB removed for now (re-add after partnership). */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.05, duration: 0.55, ease: "easeOut" }}
        className="mt-8"
        style={{ width: "100vw", marginLeft: "calc(50% - 50vw)" }}
        data-testid="trusted-data-strip"
      >
        <p
          className="text-center font-black"
          style={{ fontSize: 13, letterSpacing: "0.04em", color: "#4ade80" }}
          data-testid="trusted-data-title"
        >
          {t('Trusted UK Data Infrastructure') || 'Trusted UK Data Infrastructure'}
        </p>
        <p
          className="text-center mt-1 px-4"
          style={{ fontSize: 10, color: "rgba(226, 232, 240, 0.85)", letterSpacing: "0.06em", fontWeight: 600 }}
          data-testid="trusted-data-subtitle"
        >
          {t('Protecting buyers from fraud · Built on official UK vehicle data sources') || 'Protecting buyers from fraud · Built on official UK vehicle data sources'}
        </p>

        {/* Glass logo bar with Infinite Marquee */}
        <div
          className="mt-3 px-4 py-3 sm:px-10 panel-premium marquee-container"
          style={{
            background: "radial-gradient(ellipse at center, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.42) 55%, rgba(0, 0, 0, 0.18) 100%)",
            borderTop: "1px solid rgba(255, 255, 255, 0.15)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            boxShadow: "0 30px 70px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.10)",
          }}
        >
          <div className="marquee-content gap-12 items-center">
            {/* We render the set of logos twice to create a seamless infinite loop */}
            {[1, 2].map((setIndex) => (
              <React.Fragment key={`logo-set-${setIndex}`}>
                {/* Powered by Global Vehicle Data */}
                <div className="flex items-center gap-2" data-testid="trust-badge-gvd">
                  <span aria-hidden style={{ width: 2, height: 30, background: "#00A33B", borderRadius: 2 }} />
                  <div className="text-left">
                    <p className="uppercase font-bold" style={{ fontSize: 7.5, letterSpacing: "0.22em", color: "rgba(226, 232, 240, 0.85)", lineHeight: 1 }}>
                      {t('poweredBy') || 'Powered by'}
                    </p>
                    <p className="font-black" style={{ fontSize: 12.5, color: "rgba(226, 232, 240, 0.85)", letterSpacing: "-0.15px", lineHeight: 1.15, marginTop: 3 }}>
                      Global Vehicle Data
                    </p>
                    <p style={{ fontSize: 8.5, color: "rgba(226, 232, 240, 0.85)", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>
                      {t('gvdSubtitle') || 'UK registry data partner'}
                    </p>
                  </div>
                </div>

                <span style={{ width: 1, height: 28, background: "rgba(148,163,184,0.22)" }} aria-hidden />

                {/* DVLA */}
                <div className="flex items-center gap-2" data-testid="trust-logo-dvla">
                  <img
                    src="/logos/coat_of_arms.png"
                    alt=""
                    aria-hidden="true"
                    style={{ height: 34, width: "auto", filter: "brightness(0) invert(1)", opacity: 0.78 }}
                    draggable={false}
                  />
                  <span aria-hidden style={{ width: 2, alignSelf: "stretch", background: "#00A33B", borderRadius: 2 }} />
                  <div className="text-left">
                    <span className="font-black" style={{ fontSize: 19, color: "rgba(226, 232, 240, 0.85)", letterSpacing: "-0.3px", lineHeight: 1 }}>DVLA</span>
                    <p style={{ fontSize: 8.5, color: "rgba(226, 232, 240, 0.85)", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>
                      {t('dvlaSubtitle') || 'Driver and Vehicle Licensing Agency'}
                    </p>
                  </div>
                </div>

                <span style={{ width: 1, height: 28, background: "rgba(148,163,184,0.22)" }} aria-hidden />

                {/* DVSA */}
                <div className="flex items-center gap-2" data-testid="trust-logo-dvsa">
                  <img
                    src="/logos/coat_of_arms.png"
                    alt=""
                    aria-hidden="true"
                    style={{ height: 34, width: "auto", filter: "brightness(0) invert(1)", opacity: 0.78 }}
                    draggable={false}
                  />
                  <span aria-hidden style={{ width: 2, alignSelf: "stretch", background: "#00A33B", borderRadius: 2 }} />
                  <div className="text-left">
                    <span className="font-black" style={{ fontSize: 19, color: "rgba(226, 232, 240, 0.85)", letterSpacing: "-0.3px", lineHeight: 1 }}>DVSA</span>
                    <p style={{ fontSize: 8.5, color: "rgba(226, 232, 240, 0.85)", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>
                      {t('dvsaSubtitle') || 'Driver & Vehicle Standards Agency'}
                    </p>
                    <p className="uppercase font-black" style={{ fontSize: 8, letterSpacing: "0.22em", color: "#4ade80", marginTop: 2 }}>{t('dvsaOfficialData') || 'Official MOT data'}</p>
                  </div>
                </div>

                <span style={{ width: 1, height: 28, background: "rgba(148,163,184,0.22)" }} aria-hidden />

                {/* More Than HPI Check */}
                <div className="flex items-center gap-2" data-testid="trust-logo-mthc">
                  <svg width="34" height="37" viewBox="0 0 58 62" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <defs>
                      <linearGradient id="mthcShieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#7ED957" />
                        <stop offset="55%" stopColor="#3FAE49" />
                        <stop offset="100%" stopColor="#1C8733" />
                      </linearGradient>
                      <linearGradient id="mthcShieldHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                    </defs>
                    {/* Simplified geometric shield */}
                    <path d="M29 2L5 9.5V26C5 40 15 54 29 60C43 54 53 40 53 26V9.5L29 2Z" fill="url(#mthcShieldGrad)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <path d="M29 3L6 10V26C6 39 15.5 52.5 29 58.5C42.5 52.5 52 39 52 26V10L29 3Z" fill="none" stroke="url(#mthcShieldHighlight)" strokeWidth="1" />
                    {/* Minimalist central checkmark */}
                    <path d="M19 29.5L26.5 37L40 21" fill="none" stroke="#FFFFFF" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }} />
                  </svg>
                  <span aria-hidden style={{ width: 2, alignSelf: "stretch", background: "#00A33B", borderRadius: 2 }} />
                  <div className="text-left">
                    <span className="font-black" style={{ fontSize: 13.5, color: "rgba(226, 232, 240, 0.85)", letterSpacing: "-0.15px", lineHeight: 1.15 }}>{t('heroHpiBadgeText') || 'More Than HPI Check'}</span>
                    <p style={{ fontSize: 8.5, color: "rgba(226, 232, 240, 0.85)", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>
                      {t('hpiSubtitle') || 'Full history & valuation'}
                    </p>
                  </div>
                </div>

                <span style={{ width: 1, height: 28, background: "rgba(148,163,184,0.22)" }} aria-hidden />

                {/* ICO */}
                <div className="flex flex-col items-center justify-center" data-testid="trust-logo-ico">
                  <img
                    src="/logos/ico.svg"
                    alt="ICO — Information Commissioner's Office"
                    style={{ height: 28, width: "auto", filter: "brightness(0) invert(1)", opacity: 0.8 }}
                    draggable={false}
                  />
                  <p style={{ fontSize: 8, color: "rgba(226, 232, 240, 0.85)", fontWeight: 600, marginTop: 2 }}>Registered</p>
                </div>

                <span style={{ width: 1, height: 28, background: "rgba(148,163,184,0.22)" }} aria-hidden />

                {/* Powered by Pine.ai */}
                <div className="flex items-center gap-2" data-testid="trust-badge-pine">
                  <span aria-hidden style={{ width: 2, height: 30, background: "#00A33B", borderRadius: 2 }} />
                  <div className="text-left">
                    <p className="uppercase font-bold" style={{ fontSize: 7.5, letterSpacing: "0.22em", color: "rgba(226, 232, 240, 0.85)", lineHeight: 1 }}>
                      Powered by
                    </p>
                    <p className="font-black" style={{ fontSize: 12.5, color: "rgba(226, 232, 240, 0.85)", letterSpacing: "-0.15px", lineHeight: 1.15, marginTop: 3 }}>
                      Pine.ai
                    </p>
                    <p style={{ fontSize: 8.5, color: "rgba(226, 232, 240, 0.85)", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>
                      Voice assistant partner
                    </p>
                  </div>
                </div>
                
                {/* Spacer between the end of loop 1 and loop 2 */}
                {setIndex === 1 && <span style={{ width: 1, height: 28, background: "rgba(148,163,184,0.22)" }} aria-hidden />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </motion.div>

      <MoreThanHpiModal open={hpiInfoOpen} onClose={() => setHpiInfoOpen(false)} t={t} />
    </motion.div>
  );
};
